import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	applyProgrammeDefaults,
	getDealProgrammeDefaultsSummary,
} from '@dealstack/frontend/shared/dataExchange/services/organization.api';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useTranslationOrganization } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useLocalStorageModalController } from '@dealstack/frontend/shared/other/react/hooks/useLocalStorageModalController';
import { StorageKeys } from '@dealstack/frontend/shared/helper/storageUtils';

/**
 * The offer is per programme and one-shot: dismissing or applying it hides it for good, so a
 * half-built programme is never re-seeded behind the admin's back.
 */
export const useProgrammeDefaultsBanner = (dealId: string, mipId: string | undefined) => {
	const tOrganization = useTranslationOrganization();
	const queryClient = useQueryClient();
	const { value: programmeDefaultsEnabled } = useDealstackFeatureFlag(
		FEATURE_FLAG.PROGRAMME_DEFAULTS
	);
	const { showModal, markModalAsSeen } = useLocalStorageModalController(
		StorageKeys.DID_APPLY_PROGRAMME_DEFAULTS_ON_MIP,
		mipId
	);

	const { data: summary } = useQuery({
		queryKey: [API_PATHS.PROGRAMME_DEFAULTS_SUMMARY_DEAL_GET, dealId],
		queryFn: () => getDealProgrammeDefaultsSummary(),
		enabled: !!programmeDefaultsEnabled && showModal && !!dealId,
	});

	const applyMutation = useMutation({
		mutationFn: () => applyProgrammeDefaults(summary?.organizationId ?? ''),
		onSuccess: async () => {
			markModalAsSeen();
			showToastV2({ message: tOrganization('programme_defaults.confirm.applied') });
			// The later wizard steps read the copied rows, so drop anything already cached.
			await queryClient.invalidateQueries();
		},
	});

	return {
		summary: showModal && summary?.hasAnyConfigured ? summary : null,
		isApplying: applyMutation.isPending,
		onApply: applyMutation.mutate,
		onDismiss: markModalAsSeen,
	};
};
